import React, { useEffect, useState } from 'react';
import { Copy, Check, UserPlus, Plus, Loader2, Trash2, Users, Clock } from 'lucide-react';
import SheetOrPage from './SheetOrPage';
import {
  createConversationInviteLink, listConversationInvites, revokeConversationInvite,
  type ActiveInvite,
} from '../../services/conversationInviteService';
import { TYPE } from '../../constants/typeScale';

/** Whole days until an ISO expiry (0 = today, null = none). */
const daysLeft = (iso?: string): number | null => {
  if (!iso) return null;
  const d = Math.ceil((new Date(iso).getTime() - Date.now()) / 86_400_000);
  return d > 0 ? d : 0;
};

/**
 * Per-chat invite manager — create, share, and revoke links that add someone to THIS
 * conversation (not your account). Anyone who opens an active link and signs in joins
 * this chat as a participant. Links are multi-use until revoked or expired.
 */
const ChatInviteModal: React.FC<{ open: boolean; conversationId: string | null; title?: string; onClose: () => void; asPage?: boolean }> = ({ open, conversationId, title, onClose, asPage }) => {
  const [invites, setInvites] = useState<ActiveInvite[]>([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [copiedId, setCopiedId] = useState('');
  const [error, setError] = useState('');
  const [expiryDays, setExpiryDays] = useState(30);

  useEffect(() => {
    if (!open || !conversationId) return;
    setError(''); setLoading(true); setInvites([]);
    listConversationInvites(conversationId)
      .then(setInvites)
      .catch((e) => setError(e?.message || 'Could not load invite links'))
      .finally(() => setLoading(false));
  }, [open, conversationId]);

  if (!open) return null;

  const create = async () => {
    if (!conversationId) return;
    setCreating(true); setError('');
    try {
      await createConversationInviteLink(conversationId, expiryDays);
      // refresh to pick up the real id (needed for revoke) + server fields (uses/expiry)
      setInvites(await listConversationInvites(conversationId));
    } catch (e: any) { setError(e?.message || 'Could not create an invite link'); } finally { setCreating(false); }
  };

  const copy = async (inv: ActiveInvite) => {
    try {
      await navigator.clipboard.writeText(inv.inviteUrl);
      setCopiedId(inv.token);
      setTimeout(() => setCopiedId(''), 1500);
    } catch { /* clipboard blocked */ }
  };

  const revoke = async (inv: ActiveInvite) => {
    if (!inv.id) return;
    setError('');
    const prev = invites;
    setInvites((cur) => cur.filter((i) => i.token !== inv.token));
    try { await revokeConversationInvite(inv.id); } catch (e: any) { setError(e?.message || 'Could not revoke'); setInvites(prev); }
  };

  return (
    <SheetOrPage open={open} asPage={asPage} title="Invite to this chat" icon={<UserPlus className="h-4 w-4 text-secondary-500" />} onClose={onClose}>
        <p className={`mb-3 ${TYPE.control} text-secondary-500 dark:text-secondary-400`}>
          Share a link — anyone who opens it and signs in joins <span className="font-medium text-secondary-700 dark:text-secondary-200">this conversation{title ? ` (“${title}”)` : ''}</span>, not your account. Revoke anytime.
        </p>

        {error && <p className={`mb-2 ${TYPE.body} text-secondary-500 dark:text-secondary-400`}>{error}</p>}

        <div className="space-y-2">
          {loading ? (
            <p className={`${TYPE.body} text-secondary-400 flex items-center gap-2`}><Loader2 className="h-4 w-4 animate-spin" /> Loading links…</p>
          ) : invites.length === 0 ? (
            <p className={`${TYPE.body} text-secondary-400`}>No active invite links yet.</p>
          ) : invites.map((inv) => {
            const left = daysLeft(inv.expiresAt);
            const joined = inv.useCount || 0;
            return (
              <div key={inv.token} className="rounded-lg border border-secondary-200 dark:border-secondary-600 p-2">
                <div className="flex items-center gap-2">
                  <input readOnly value={inv.inviteUrl} onFocus={(e) => e.currentTarget.select()}
                    className={`flex-1 min-w-0 rounded-lg border border-secondary-300 dark:border-secondary-600 bg-secondary-50 dark:bg-secondary-800 px-3 py-2 ${TYPE.control} text-secondary-700 dark:text-secondary-200`} />
                  <button onClick={() => copy(inv)} aria-label="Copy link" title="Copy link" className={`shrink-0 inline-flex items-center gap-1 rounded-lg bg-secondary-900 px-2.5 py-2 ${TYPE.control} font-medium text-white hover:bg-black dark:bg-white dark:text-secondary-900 dark:hover:bg-secondary-100`}>
                    {copiedId === inv.token ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                  </button>
                  <button onClick={() => revoke(inv)} aria-label="Revoke link" title="Revoke link" disabled={!inv.id} className="shrink-0 text-secondary-400 hover:text-secondary-500 disabled:opacity-40"><Trash2 className="h-4 w-4" /></button>
                </div>
                <div className={`mt-1 flex items-center gap-3 px-1 ${TYPE.meta} text-secondary-400`}>
                  <span className="inline-flex items-center gap-1"><Users className="h-3 w-3" /> {joined} joined</span>
                  <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> {left === null ? 'no expiry' : left === 0 ? 'expires today' : `expires in ${left} day${left === 1 ? '' : 's'}`}</span>
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-3 flex items-center gap-2">
          <button onClick={create} disabled={creating || !conversationId}
            className={`inline-flex items-center gap-1.5 rounded-lg border border-dashed border-secondary-300 dark:border-secondary-600 px-3 py-2 ${TYPE.body} text-secondary-600 dark:text-secondary-300 hover:border-secondary-500 disabled:opacity-40`}>
            {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Create an invite link
          </button>
          <label className={`${TYPE.control} text-secondary-400`}>lasts
            <select value={expiryDays} onChange={(e) => setExpiryDays(Number(e.target.value))}
              className={`ml-1.5 rounded-md border border-secondary-300 dark:border-secondary-600 bg-white dark:bg-secondary-800 px-1.5 py-1 ${TYPE.control} text-secondary-700 dark:text-secondary-200`}>
              <option value={7}>7 days</option>
              <option value={30}>30 days</option>
              <option value={90}>90 days</option>
            </select>
          </label>
        </div>
    </SheetOrPage>
  );
};

export default ChatInviteModal;
